import React, { useState } from 'react';
import {
    Box,
    Card,
    Typography,
    Stack,
    TextField,
    Button,
    Grid,
    MenuItem,
    FormControlLabel,
    Checkbox,
    FormGroup,
    Divider,
    Alert,
    CircularProgress,
    Paper,
    Avatar
} from '@mui/material';
import { Megaphone, Send, Mail, Bell, MessageSquare, Users } from 'lucide-react';
import NotificationService from 'api/NotificationService';
import { toast } from 'react-toastify';

const BroadcastMessaging = () => {
    const [loading, setLoading] = useState(false);
    
    const [formData, setFormData] = useState({
        target: 'all_customers',
        title: '',
        message: '',
        identifier: '',
        channels: ['in_app', 'push']
    });

    const targets = [
        { value: 'all', label: 'Everyone (All Users)', color: '#673ab7' },
        { value: 'all_customers', label: 'All Customers', color: '#2196f3' },
        { value: 'all_vendors', label: 'All Vendors', color: '#ffc107' },
        { value: 'all_riders', label: 'All Riders', color: '#4caf50' },
        { value: 'all_admins', label: 'All Admins', color: '#f44336' },
        { value: 'specific_user', label: 'Specific User (Email/Phone)', color: '#607d8b' }
    ];

    const handleChannelChange = (channel) => {
        setFormData((prev) => {
            const newChannels = prev.channels.includes(channel)
                ? prev.channels.filter((c) => c !== channel)
                : [...prev.channels, channel];
            return { ...prev, channels: newChannels };
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (formData.channels.length === 0) {
            toast.error('Please select at least one delivery channel.');
            return;
        }

        if (formData.target === 'specific_user' && !formData.identifier) {
            toast.error('Please enter a User Email or Phone Number.');
            return;
        }

        setLoading(true);

        try {
            const payload = {
                title: formData.title,
                message: formData.message,
                channels: formData.channels,
                target: formData.target !== 'specific_user' ? formData.target : undefined,
                identifier: formData.target === 'specific_user' ? formData.identifier : undefined
            };

            await NotificationService.sendNotification(payload);
            
            const targetLabel = targets.find(t => t.value === formData.target)?.label || 'Specific User';
            toast.success(`Message successfully sent to ${targetLabel}`);
            setFormData({ ...formData, title: '', message: '', identifier: '' });
        } catch (err) {
            toast.error(err?.response?.data?.message || err.message || 'Failed to send message');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box p={{ xs: 2, md: 4 }}>
            <Stack direction="row" alignItems="center" spacing={2} mb={4}>
                <Avatar sx={{ bgcolor: 'primary.light', color: 'primary.main', width: 48, height: 48 }}>
                    <Megaphone size={24} />
                </Avatar>
                <Box>
                    <Typography variant="h3" fontWeight={800} color="primary.dark">
                        Broadcast Center
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Reach your users across multiple channels instantly
                    </Typography>
                </Box>
            </Stack>

            <Grid container spacing={4}>
                <Grid item xs={12} md={7}>
                    <Card sx={{ p: 4, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
                        <form onSubmit={handleSubmit}>
                            <Stack spacing={3}>
                                <TextField
                                    select
                                    fullWidth
                                    label="Target Audience"
                                    value={formData.target}
                                    onChange={(e) => setFormData({ ...formData, target: e.target.value })}
                                    required
                                >
                                    {targets.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            <Stack direction="row" alignItems="center" spacing={1.5}>
                                                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: option.color }} />
                                                <Typography>{option.label}</Typography>
                                            </Stack>
                                        </MenuItem>
                                    ))}
                                </TextField>

                                {formData.target === 'specific_user' && (
                                    <TextField
                                        fullWidth
                                        label="User Email or Phone Number"
                                        placeholder="e.g. user@example.com or +234..."
                                        value={formData.identifier}
                                        onChange={(e) => setFormData({ ...formData, identifier: e.target.value })}
                                        required
                                    />
                                )}

                                <TextField
                                    fullWidth
                                    label="Message Title / Subject"
                                    placeholder="e.g. System Update, Special Offer!"
                                    value={formData.title}
                                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                    required
                                />

                                <TextField
                                    fullWidth
                                    multiline
                                    rows={5}
                                    label="Message Content"
                                    placeholder="Type your message here..."
                                    value={formData.message}
                                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                                    required
                                />

                                <Box>
                                    <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                                        Delivery Channels
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
                                        Select how you want this message to be delivered
                                    </Typography>
                                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: '#fafafa' }}>
                                        <FormGroup row>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={formData.channels.includes('in_app')}
                                                        onChange={() => handleChannelChange('in_app')}
                                                    />
                                                }
                                                label={
                                                    <Stack direction="row" alignItems="center" spacing={1}>
                                                        <MessageSquare size={16} />
                                                        <Typography variant="body2">In-App Notification</Typography>
                                                    </Stack>
                                                }
                                            />
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={formData.channels.includes('push')}
                                                        onChange={() => handleChannelChange('push')}
                                                    />
                                                }
                                                label={
                                                    <Stack direction="row" alignItems="center" spacing={1}>
                                                        <Bell size={16} />
                                                        <Typography variant="body2">Push Notification</Typography>
                                                    </Stack>
                                                }
                                            />
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={formData.channels.includes('email')}
                                                        onChange={() => handleChannelChange('email')}
                                                    />
                                                }
                                                label={
                                                    <Stack direction="row" alignItems="center" spacing={1}>
                                                        <Mail size={16} />
                                                        <Typography variant="body2">Email</Typography>
                                                    </Stack>
                                                }
                                            />
                                        </FormGroup>
                                    </Paper>
                                </Box>

                                <Button
                                    type="submit"
                                    variant="contained"
                                    size="large"
                                    startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Send size={20} />}
                                    disabled={loading}
                                    sx={{ py: 1.5, fontSize: '1rem', fontWeight: 700 }}
                                >
                                    {loading ? 'Sending Message...' : formData.target === 'specific_user' ? 'Send to User Now' : 'Send Broadcast Now'}
                                </Button>
                            </Stack>
                        </form>
                    </Card>
                </Grid>

                <Grid item xs={12} md={5}>
                    <Stack spacing={3}>
                        <Card sx={{ p: 3, borderRadius: 3, bgcolor: 'primary.main', color: 'white' }}>
                            <Stack direction="row" spacing={2} alignItems="center">
                                <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.2)' }}>
                                    <Users size={24} />
                                </Box>
                                <Box>
                                    <Typography variant="h4" fontWeight={700}>Target Estimator</Typography>
                                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                                        {formData.target === 'specific_user' 
                                            ? `Targeting: ${formData.identifier || 'Enter details'}` 
                                            : `Selected: ${targets.find(t => t.value === formData.target)?.label}`}
                                    </Typography>
                                </Box>
                            </Stack>
                            <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,0.1)' }} />
                            <Typography variant="body2">
                                Your message will be sent to thousands of users across your platform. Please review the content carefully before sending.
                            </Typography>
                        </Card>

                        <Card sx={{ p: 3, borderRadius: 3, border: '1px dashed #ccc', bgcolor: 'transparent' }}>
                            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                                Best Practices
                            </Typography>
                            <Box component="ul" sx={{ pl: 2, '& li': { mb: 1, fontSize: '0.875rem', color: 'text.secondary' } }}>
                                <li>Use clear and concise titles.</li>
                                <li>Avoid excessive use of "Urgent" unless necessary.</li>
                                <li>For marketing emails, ensure the content is engaging.</li>
                                <li>Double-check links if any are included in the message.</li>
                            </Box>
                        </Card>

                        <Card sx={{ p: 3, borderRadius: 3 }}>
                            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                                Active Channels
                            </Typography>
                            <Stack spacing={2} mt={2}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Typography variant="body2">Firebase Push (FCM)</Typography>
                                    <Chip label="ONLINE" color="success" size="tiny" sx={{ height: 18, fontSize: '0.6rem' }} />
                                </Stack>
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Typography variant="body2">SMTP Email (Nodemailer)</Typography>
                                    <Chip label="ONLINE" color="success" size="tiny" sx={{ height: 18, fontSize: '0.6rem' }} />
                                </Stack>
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Typography variant="body2">WebSockets (Socket.io)</Typography>
                                    <Chip label="ONLINE" color="success" size="tiny" sx={{ height: 18, fontSize: '0.6rem' }} />
                                </Stack>
                            </Stack>
                        </Card>
                    </Stack>
                </Grid>
            </Grid>
        </Box>
    );
};

const Chip = ({ label, color }) => (
    <Box sx={{ 
        px: 1, 
        py: 0.25, 
        borderRadius: 1, 
        bgcolor: color === 'success' ? '#e8f5e9' : '#f5f5f5', 
        color: color === 'success' ? '#2e7d32' : '#757575',
        fontSize: '0.65rem',
        fontWeight: 700
    }}>
        {label}
    </Box>
);

export default BroadcastMessaging;
